import React, { MouseEvent } from 'react'
import { Box } from '@mui/material'
import styled from '@emotion/styled'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import CameraOutlinedIcon from '@mui/icons-material/Camera'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import { colors, Tabs, Tab } from '@resiliatech/birdsong'
import Divider from '@mui/material/Divider'

type GoalInfoProps = {
  onSelect?: (tab: string) => void
  currentTab?: string
}

export const GoalInfo = ({ onSelect, currentTab = '' }: GoalInfoProps) => {
  const tabOptions = [
    {
      id: 'progress',
      label: 'Progress',
      icon: <TrendingUpIcon />,
      testId: 'progress-tab',
    },
    {
      id: 'snapshots',
      label: 'Snapshots',
      icon: <CameraOutlinedIcon />,
      testId: 'snapshots-tab',
    },
    {
      id: 'attachments',
      label: 'Attachments',
      icon: <AttachFileOutlinedIcon />,
      testId: 'attachments-tab',
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: <ChecklistOutlinedIcon />,
      testId: 'tasks-tab',
    },
  ]

  // Handle direct tab clicks with toggle functionality
  const handleTabClick = (event: MouseEvent, tabId: string) => {
    event.preventDefault()
    event.stopPropagation()

    if (!onSelect) return

    // We pass the tab ID to the parent's onSelect handler (which is openTab in NewGoalCard)
    // openTab will handle the toggling logic
    onSelect(tabId)
  }

  // This is required for the MUI Tabs component
  const handleTabChange = () => {
    // The actual handling is done in handleTabClick
  }

  return (
    <TabsContainer>
      <StyledDivider />
      <StyledTabs
        value={
          currentTab === ''
            ? false
            : tabOptions.findIndex((tab) => tab.id === currentTab)
        }
        onChange={handleTabChange}
        textColor='primary'
        indicatorColor='primary'
        aria-label='goal info tabs'
        TabIndicatorProps={{
          style: {
            height: '2px',
            borderRadius: '2px',
          },
        }}
      >
        {tabOptions.map((tab) => (
          <StyledTab
            key={tab.id}
            data-testid={tab.testId}
            onClick={(e) => handleTabClick(e, tab.id)}
            label={
              <TabLabelWrapper>
                <TabIcon>{tab.icon}</TabIcon>
                <span>{tab.label}</span>
              </TabLabelWrapper>
            }
            sx={{
              backgroundColor:
                currentTab === tab.id ? '#E8F3FF' : 'transparent',
              // eslint-disable-next-line
              '&.active': {
                color: colors.interactive.primaryEnabled,
                border: `1px solid ${colors.interactive.borderEnabledHoverGray}`,
                borderBottom: 'none',
              },
            }}
            className={currentTab === tab.id ? 'active' : ''}
          />
        ))}
      </StyledTabs>
    </TabsContainer>
  )
}

const TabsContainer = styled(Box)`
  width: 100%;
`
const StyledDivider = styled(Divider)`
  position: relative;
  top: 8px;
`

const StyledTabs = styled(Tabs)`
  min-height: 60px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  position: relative;
  padding-left: 0;
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${colors.interactive.borderEnabledHoverGray};
    z-index: 0;
  }

  & .MuiTabs-flexContainer {
    height: 100%;
    position: relative;
    z-index: 1;
  }

  & .MuiTabs-indicator {
    background: none;
  }
`

const StyledTab = styled(Tab)`
  text-transform: none;
  min-height: 48px;
  padding: 0 40px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  border-right: none !important;
  position: relative;
  font-family: 'Helvetica Neue';
  font-weight: 400;
  color: #003f7e;

  @media (max-width: 1200px) {
    padding: 0 10px;
  }

  &:not(:last-child) {
    border-right: 1px solid ${colors.interactive.borderEnabledHoverGray};
  }

  &:hover {
    color: blue;
  }

  /* Use a custom class instead of the Mui-selected class */
  &.active {
    font-weight: 400;
    border-right: 1px solid ${colors.interactive.borderEnabledHoverGray} !important;

    /* This creates a white bar that covers the border under the active tab */
    &:after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background-color: #e8f3ff;
      z-index: 2;
    }
  }
`

const TabLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 8px;
`

const TabIcon = styled.span`
  display: flex;
  align-items: center;
  margin-right: 8px;

  svg {
    font-size: 20px;
  }
`
